import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, CheckCircle2, Truck, Package, Store, CreditCard, Landmark, FileText } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { productById, fmt, bezPvn } from "../data/store";
import { Checkbox } from "../components/ui/checkbox";

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-sm font-semibold mb-1 block">{label}</span>
    <input {...props} data-testid={testId}
      className="w-full h-10 px-3 rounded-md border border-[#E2E2E2] text-sm focus:outline-none focus:ring-2 focus:ring-[#111111]" />
  </label>
);

const DELIVERY = [
  { id: "kurjers", icon: Truck, name: "Kurjers", desc: "1–3 darba dienas", price: 4.99 },
  { id: "pakomats", icon: Package, name: "Pakomāts", desc: "1–3 darba dienas", price: 2.99 },
  { id: "veikals", icon: Store, name: "Saņemšana demo veikalā", desc: "Tajā pašā dienā", price: 0 },
];
const PAYMENT = [
  { id: "banka", icon: Landmark, name: "Internetbanka", desc: "Swedbank, SEB, Citadele, Luminor" },
  { id: "karte", icon: CreditCard, name: "Maksājumu karte", desc: "Visa, Mastercard" },
  { id: "rekins", icon: FileText, name: "Rēķins uzņēmumiem", desc: "Apmaksa 7 dienu laikā" },
];

export default function CheckoutPage() {
  const { items, clear, discount } = useCart();
  const [step, setStep] = useState(1);
  const [isCompany, setIsCompany] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", regNr: "", pvnNr: "" });
  const [delivery, setDelivery] = useState("kurjers");
  const [address, setAddress] = useState("");
  const [payment, setPayment] = useState("banka");
  const [orderNr, setOrderNr] = useState(null);

  const lines = items.map((i) => ({ ...i, p: productById(i.id) })).filter((l) => l.p);
  const subtotal = lines.reduce((s, l) => s + l.p.price * l.qty, 0);
  const shipCost = subtotal >= 60 ? 0 : (DELIVERY.find((d) => d.id === delivery)?.price || 0);
  const total = subtotal * (1 - discount) + shipCost;

  const next1 = (e) => {
    e.preventDefault();
    setStep(2);
  };
  const next2 = (e) => {
    e.preventDefault();
    if (delivery !== "veikals" && !address.trim()) { toast.error("Norādi piegādes adresi"); return; }
    setStep(3);
  };
  const placeOrder = () => {
    const nr = "DEMO-" + Math.floor(100000 + Math.random() * 900000);
    const order = {
      nr, date: new Date().toISOString(), customer: contact.name, email: contact.email,
      company: isCompany ? contact.company : null, delivery, payment, status: "Jauns",
      items: lines.map((l) => ({ name: l.p.name, sku: l.p.sku, qty: l.qty, price: l.p.price })),
      total,
    };
    try {
      const prev = JSON.parse(localStorage.getItem("demo_orders")) || [];
      localStorage.setItem("demo_orders", JSON.stringify([order, ...prev]));
    } catch { /* noop */ }
    setOrderNr(nr);
    clear();
  };

  if (orderNr) {
    return (
      <div className="max-w-xl mx-auto px-4 mt-16 text-center" data-testid="order-confirmation">
        <CheckCircle2 size={64} className="text-[#D0121A] mx-auto" />
        <h1 className="text-3xl font-extrabold mt-4">Paldies par pasūtījumu!</h1>
        <p className="text-[#6B6B6B] mt-2">Pasūtījuma numurs: <span className="font-bold text-[#111111]" data-testid="order-number">{orderNr}</span></p>
        <p className="text-sm text-[#6B6B6B] mt-1">Apstiprinājums nosūtīts uz {contact.email || "tavu e-pastu"}. Mēs sazināsimies par piegādi.</p>
        <Link to="/" data-testid="confirmation-home-btn" className="inline-block mt-6 bg-[#111111] hover:bg-[#3A3A3A] text-white font-semibold px-8 py-3 rounded-md transition-colors">Atpakaļ uz veikalu</Link>
      </div>
    );
  }

  if (lines.length === 0) {
    return (
      <div className="max-w-xl mx-auto px-4 mt-16 text-center">
        <p className="text-lg font-bold">Grozs ir tukšs</p>
        <Link to="/" className="inline-block mt-4 bg-[#111111] text-white font-semibold px-6 py-3 rounded-md">Uz veikalu</Link>
      </div>
    );
  }

  const steps = ["Kontaktinformācija", "Piegāde", "Apmaksa"];

  return (
    <div className="max-w-7xl mx-auto px-4 mt-6">
      <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight uppercase mb-6">Pasūtījuma noformēšana</h1>
      <div className="flex items-center gap-2 mb-8" data-testid="checkout-steps">
        {steps.map((s, i) => (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step > i + 1 ? "bg-[#D0121A] text-white" : step === i + 1 ? "bg-[#111111] text-white" : "bg-[#F5F5F5] text-[#6B6B6B]"}`}>
              {step > i + 1 ? <Check size={13} /> : i + 1}
            </span>
            <span className={`text-sm font-semibold hidden sm:block ${step === i + 1 ? "text-[#111111]" : "text-[#6B6B6B]"}`}>{s}</span>
            {i < 2 && <span className="w-8 h-px bg-[#E2E2E2]" />}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          {step === 1 && (
            <form onSubmit={next1} className="space-y-4 bg-white border border-[#E2E2E2] rounded-md p-6" data-testid="checkout-step-1">
              <h2 className="text-lg font-bold">Kontaktinformācija</h2>
              <p className="text-xs text-[#6B6B6B]">Viesa pirkums — reģistrācija nav nepieciešama.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Vārds, uzvārds *" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} testId="checkout-name" />
                <Field label="Tālrunis *" required type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} testId="checkout-phone" />
              </div>
              <Field label="E-pasts *" required type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} testId="checkout-email" />
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer pt-2">
                <Checkbox checked={isCompany} onCheckedChange={setIsCompany} data-testid="company-toggle" />
                Pērku kā uzņēmums
              </label>
              {isCompany && (
                <div className="grid sm:grid-cols-3 gap-4 bg-[#F5F5F5] rounded-md p-4" data-testid="company-fields">
                  <Field label="Uzņēmuma nosaukums *" required value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })} testId="checkout-company-name" />
                  <Field label="Reģ. nr. *" required value={contact.regNr} onChange={(e) => setContact({ ...contact, regNr: e.target.value })} testId="checkout-reg-nr" />
                  <Field label="PVN nr." value={contact.pvnNr} onChange={(e) => setContact({ ...contact, pvnNr: e.target.value })} testId="checkout-pvn-nr" />
                </div>
              )}
              <button type="submit" data-testid="checkout-step1-next" className="bg-[#111111] hover:bg-[#3A3A3A] text-white font-bold px-8 py-3 rounded-md transition-colors">Turpināt → Piegāde</button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={next2} className="space-y-4 bg-white border border-[#E2E2E2] rounded-md p-6" data-testid="checkout-step-2">
              <h2 className="text-lg font-bold">Piegādes veids</h2>
              <div className="space-y-3">
                {DELIVERY.map((d) => (
                  <label key={d.id} className={`flex items-center gap-4 border rounded-md p-4 cursor-pointer transition-colors ${delivery === d.id ? "border-[#111111] bg-[#111111]/5" : "border-[#E2E2E2] hover:border-[#111111]"}`}>
                    <input type="radio" name="delivery" checked={delivery === d.id} onChange={() => setDelivery(d.id)} data-testid={`delivery-${d.id}`} className="accent-[#111111]" />
                    <d.icon size={20} className="text-[#111111]" />
                    <div className="flex-1">
                      <div className="text-sm font-bold">{d.name}</div>
                      <div className="text-xs text-[#6B6B6B]">{d.desc}</div>
                    </div>
                    <span className="text-sm font-bold">{subtotal >= 60 || d.price === 0 ? "Bezmaksas" : fmt(d.price)}</span>
                  </label>
                ))}
              </div>
              {delivery !== "veikals" && (
                <Field label={delivery === "pakomats" ? "Pakomāta adrese *" : "Piegādes adrese *"} value={address} onChange={(e) => setAddress(e.target.value)} testId="checkout-address" placeholder="Iela, māja, pilsēta, pasta indekss" />
              )}
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} data-testid="checkout-step2-back" className="border-2 border-[#E2E2E2] hover:border-[#111111] font-semibold px-6 py-3 rounded-md transition-colors">← Atpakaļ</button>
                <button type="submit" data-testid="checkout-step2-next" className="bg-[#111111] hover:bg-[#3A3A3A] text-white font-bold px-8 py-3 rounded-md transition-colors">Turpināt → Apmaksa</button>
              </div>
            </form>
          )}

          {step === 3 && (
            <div className="space-y-4 bg-white border border-[#E2E2E2] rounded-md p-6" data-testid="checkout-step-3">
              <h2 className="text-lg font-bold">Apmaksas veids</h2>
              <div className="space-y-3">
                {PAYMENT.filter((p) => p.id !== "rekins" || isCompany).map((p) => (
                  <label key={p.id} className={`flex items-center gap-4 border rounded-md p-4 cursor-pointer transition-colors ${payment === p.id ? "border-[#111111] bg-[#111111]/5" : "border-[#E2E2E2] hover:border-[#111111]"}`}>
                    <input type="radio" name="payment" checked={payment === p.id} onChange={() => setPayment(p.id)} data-testid={`payment-${p.id}`} className="accent-[#111111]" />
                    <p.icon size={20} className="text-[#111111]" />
                    <div>
                      <div className="text-sm font-bold">{p.name}</div>
                      <div className="text-xs text-[#6B6B6B]">{p.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {!isCompany && <p className="text-xs text-[#6B6B6B]">Rēķina apmaksa pieejama tikai uzņēmumiem (atzīmē “Pērku kā uzņēmums” 1. solī).</p>}
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} data-testid="checkout-step3-back" className="border-2 border-[#E2E2E2] hover:border-[#111111] font-semibold px-6 py-3 rounded-md transition-colors">← Atpakaļ</button>
                <button onClick={placeOrder} data-testid="place-order-btn" className="flex-1 bg-[#D0121A] hover:bg-[#A80D14] text-white font-bold px-8 py-3 rounded-md transition-colors">Apstiprināt pasūtījumu · {fmt(total)}</button>
              </div>
            </div>
          )}
        </div>

        <div className="bg-[#F5F5F5] rounded-md p-6 h-fit" data-testid="checkout-summary">
          <h2 className="text-lg font-bold mb-4">Kopsavilkums</h2>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
            {lines.map((l) => (
              <div key={l.id} className="flex gap-3 text-sm">
                <img src={l.p.image} alt="" className="w-12 h-12 object-cover rounded bg-white" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold line-clamp-1">{l.p.name}</div>
                  <div className="text-xs text-[#6B6B6B]">{l.qty} × {fmt(l.p.price)}</div>
                </div>
                <span className="font-bold">{fmt(l.p.price * l.qty)}</span>
              </div>
            ))}
          </div>
          <div className="space-y-2 text-sm border-t border-[#E2E2E2] pt-4 mt-4">
            <div className="flex justify-between"><span className="text-[#6B6B6B]">Starpsumma</span><span className="font-semibold">{fmt(subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-[#6B6B6B]">t.sk. PVN 21%</span><span>{fmt(subtotal - bezPvn(subtotal))}</span></div>
            {discount > 0 && <div className="flex justify-between text-[#D0121A]"><span>Atlaide –10%</span><span>–{fmt(subtotal * discount)}</span></div>}
            <div className="flex justify-between"><span className="text-[#6B6B6B]">Piegāde</span><span className="font-semibold">{shipCost === 0 ? "Bezmaksas" : fmt(shipCost)}</span></div>
            <div className="flex justify-between text-lg font-extrabold border-t border-[#E2E2E2] pt-3"><span>Kopā (ar PVN)</span><span data-testid="checkout-total">{fmt(total)}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
